/**
 * `/easy-quote/?quoteId=<id>`: where a Ryerson customer lands from the "purchase" link in a quote
 * email, to order the quote with or without signing in. The link is built by
 * `web-quotes-api/application/src/services/email.service.ts` → `sendEmail` while the
 * `10051-easy-quote` toggle is on (otherwise the email links to `/store/dashboard/quotes/<id>`).
 * The 401 hand-back from the store's guest checkout returns here too: the guest session's
 * `redirectUrl` is this URL (`components/Login/EasyQuoteHelpers/EasyQuoteGuestForm.tsx`).
 *
 * In order, once the router has parsed the query string (a static export has no query at
 * prerender; see `copilot/knowledge-base/Routing.md`):
 * 1. Signed in: `components/Login/hooks/useRedirectIfLoggedInEasyQuote.ts` sends the browser to
 *    the quote in the store, and the page renders nothing.
 * 2. No `quoteId`: client-side push to `/`.
 * 3. Otherwise `components/NextLayouts/PublicWithFeature.tsx` gates the screen on
 *    `EasyQuote.constants.ts` → `EasyQuoteFeatureFlag`: "Loading" until the toggles arrive, then
 *    `Login` in `"easyQuote"` mode when the toggle is on, or a full load of `/` when it is off.
 *
 * `component` is an inline arrow, so each render of this page gives `PublicWithFeature` a new
 * component type and React remounts `Login`; `Login.tsx` keeps its step in sessionStorage
 * `displayStep` and so survives that. The meta is the sign-in page's
 * (`content/shared/login` → `LoginMetaObject`).
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import MetaHead from "@components/Shared/model/MetaHead";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import Login from "@components/Login/Login";
import useRedirectIfLoggedInEasyQuote from "@components/Login/hooks/useRedirectIfLoggedInEasyQuote";
import { LoginMetaObject } from "@content/shared/login";
import { useRouter } from "next/router";
import PublicWithFeature from "@components/NextLayouts/PublicWithFeature";
import { EasyQuoteFeatureFlag } from "@components/Login/EasyQuoteHelpers/EasyQuote.constants";

/** Build-time Contentful header, locations and footer data for `HeaderLayout`. */
export const getStaticProps = async () => {
	return await allContentfulHeaderCalls();
};

/** The Easy Quote landing page; see the file header. */
const Page: React.FC<HeaderPageProps> = (props) => {
	const {
		localization: { language },
	} = useApplication();

	const router = useRouter();
	
	// query string
	const { quoteId } = router.query;
	
	// Call hook to see if user should be redirected based on login status and presence of quoteId in query params
	const shouldRedirect = useRedirectIfLoggedInEasyQuote(quoteId as string);

	// Wait for router to be ready to prevent flash during hydration
	if (!router.isReady) {
		return null;
	}

	// After router is ready, if no quoteId provided, redirect to home
	if (!quoteId) {
		router.push("/");
		return null;
	}
	return !shouldRedirect ? (
		<HeaderLayout {...getHeaderData(props)}>
			<PublicWithFeature
				featureString={EasyQuoteFeatureFlag}
				component={(props) => (
					<>
						<MetaHead content={LoginMetaObject[language]} />
						<Login mode="easyQuote" />
					</>
				)}
			/>
		</HeaderLayout>
	) : null;
};

export default Page;
